export type ReviewTextSegment = {
  text: string;
  isHighlighted: boolean;
};

type Range = {
  start: number;
  end: number;
};

const REGEXP_SPECIAL_CHARACTERS = /[.*+?^${}()|[\]\\]/g;

/**
 * Elasticsearch returns the matched passages verbatim, but the text they came
 * from may have been re-wrapped for display, so a passage is matched with its
 * whitespace runs left flexible.
 */
const buildPassagePattern = (passage: string): RegExp =>
  new RegExp(
    passage.trim().replace(REGEXP_SPECIAL_CHARACTERS, '\\$&').replace(/\s+/g, '\\s+'),
    'gi'
  );

const overlaps = (range: Range, ranges: Range[]): boolean =>
  ranges.some((other) => range.start < other.end && other.start < range.end);

const findRange = (text: string, passage: string, taken: Range[]): Range | null => {
  const pattern = buildPassagePattern(passage);
  let match = pattern.exec(text);

  while (match) {
    const range = { start: match.index, end: match.index + match[0].length };
    if (!overlaps(range, taken)) {
      return range;
    }
    match = pattern.exec(text);
  }

  return null;
};

/**
 * Splits a review into the passages that matched the searcher's pets and the
 * text around them. Each highlight is a whole passage of plain text, and it is
 * emphasized in full. Passages that no longer appear in the review are dropped,
 * so an empty or stale highlight list yields the review unchanged.
 */
export const getReviewTextSegments = (
  text: string,
  highlights?: string[] | null
): ReviewTextSegment[] => {
  const matched: Range[] = [];

  (highlights ?? []).forEach((passage) => {
    if (!passage.trim()) return;

    const range = findRange(text, passage, matched);
    if (range) {
      matched.push(range);
    }
  });

  matched.sort((a, b) => a.start - b.start);

  const segments: ReviewTextSegment[] = [];
  let cursor = 0;

  matched.forEach(({ start, end }) => {
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), isHighlighted: false });
    }
    segments.push({ text: text.slice(start, end), isHighlighted: true });
    cursor = end;
  });

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), isHighlighted: false });
  }

  return segments;
};
